import React, { useEffect, useRef, useState } from 'react';
import db from './firebase.config.js';
import firebase from 'firebase/app';
import 'firebase/firestore';
import { useSelector, useDispatch } from 'react-redux';
import PlayArrow from '@mui/icons-material/PlayArrow';
import Pause from '@mui/icons-material/Pause';
import SkipNext from '@mui/icons-material/SkipNext';
import Tune from '@mui/icons-material/Tune';
import spotify_logo from './spotify.svg';
import PopupMenu from './PopupMenu';
import SkipTimer from './SkipTimer';
import useCountdown from './useCountdown';
import { buildSkipPatch, SKIP_COOLDOWN_MS, TRACK_CHANGE_COOLDOWN_MS } from './remoteContract';

function savedDeadline(key) {
    try { return Number(sessionStorage.getItem(key)) || 0; } catch { return 0; }
}

export function Skip({ uid, trackKey, onStatus }) {
    const storageKey = `strive:skip:${uid}`;
    const [ownDeadline, setOwnDeadline] = useState(() => savedDeadline(storageKey));
    const [trackDeadline, setTrackDeadline] = useState(() => Date.now() + TRACK_CHANGE_COOLDOWN_MS);
    const [pending, setPending] = useState(false);
    const [error, setError] = useState('');
    const inFlight = useRef(false);
    const seconds = useCountdown(Math.max(ownDeadline, trackDeadline));
    const dispatch = useDispatch();
    useEffect(() => { setTrackDeadline(Date.now() + TRACK_CHANGE_COOLDOWN_MS); }, [uid, trackKey]);
    useEffect(() => { setOwnDeadline(savedDeadline(storageKey)); }, [storageKey]);
    const status = error || (pending ? 'Sending skip…' : seconds > 0 ? `Skip available in ${seconds}s · Give each song a moment.` : 'Take turns. Discover something new.');
    useEffect(() => { onStatus(status); }, [status, onStatus]);

    async function skip() {
        if (!uid || inFlight.current || Date.now() < Math.max(ownDeadline, trackDeadline)) return;
        inFlight.current = true;
        setPending(true);
        setError('');
        try {
            await db.collection('Users').doc(uid).update(buildSkipPatch());
            const until = Date.now() + SKIP_COOLDOWN_MS;
            setOwnDeadline(until);
            try { sessionStorage.setItem(storageKey, String(until)); } catch { /* Storage may be disabled. */ }
            dispatch({ type: 'Increment' });
        } catch { setError('Couldn’t skip. Try again.'); }
        finally { inFlight.current = false; setPending(false); }
    }
    return <button className="skip-btn" onClick={skip} disabled={!uid || pending || seconds > 0} aria-label={seconds > 0 ? `Skip available in ${seconds} seconds` : 'Skip song'}>{seconds > 0 ? <SkipTimer sec={seconds} /> : <SkipNext />}</button>;
}

export default function Remote({ uid, passenger: pax_id, refresh: refreshToken, vol_control }) {
    const CDN = useSelector(state => state.cdn);
    const track_url = useSelector(state => state.url);
    const artistName = useSelector(state => state.artist);
    const prev_song = useSelector(state => state.song);
    const [paused, setPaused] = useState(false);
    const [pending, setPending] = useState(false);
    const [error, setError] = useState('');
    const [skipStatus, setSkipStatus] = useState('');
    const [settingsOpen, setSettingsOpen] = useState(false);
    const playbackInFlight = useRef(false);
    function spotifyClicked() {
        var curr_track_parts = track_url.split("/")
        var curr_track_id_utm = curr_track_parts[curr_track_parts.length - 1]
        var curr_track_id_parts = curr_track_id_utm.split("?")
        var curr_track_id = curr_track_id_parts[0]

        if(pax_id && pax_id !== ""){
            const pax_doc = db.collection('Passengers').doc(pax_id)
            pax_doc.set({
                'tracksClicked' : firebase.firestore.FieldValue.arrayUnion(curr_track_id)
            }, {merge: true})
        }

        db.collection('Artist_Analytics').doc(curr_track_id).collection('passenger_id').doc(pax_id || 'unknown').set({
            artist_name: artistName,
            song_name: prev_song,
            track_id: curr_track_id,
            spotify_clicks: firebase.firestore.FieldValue.increment(1),
            driver: uid,
            passenger_id: pax_id,
            time_clicked: firebase.firestore.FieldValue.serverTimestamp(),
        }, {merge: true})
    }

    async function togglePlayback() {
        if (!refreshToken || playbackInFlight.current) return;
        playbackInFlight.current = true;
        setPending(true);
        setError('');
        try {
            const response = await fetch(`https://api.spotify.com/v1/me/player/${paused ? 'play' : 'pause'}`, {
                method: 'PUT', headers: { Authorization: `Bearer ${refreshToken}` },
            });
            if (!response.ok) throw new Error('Playback rejected');
            setPaused(!paused);
        } catch { setError('Playback didn’t respond. Try again.'); }
        finally { playbackInFlight.current = false; setPending(false); }
    }

    return <footer className="anchor" aria-label="Music player">
        <div className="remote">
            <div className="player-overline"><span><span className="live-dot" /> {paused ? 'Paused' : 'Now playing'}</span><button className={`settings-btn ${settingsOpen ? 'active' : ''}`} onClick={() => setSettingsOpen(true)} aria-label="Playback settings" aria-haspopup="dialog"><Tune style={{fontSize: 19}} /></button></div>
            <div className="remote_track_info">
                <a className="current-art-link" href={track_url || undefined} target="_blank" rel="noopener noreferrer" onClick={track_url ? spotifyClicked : undefined} aria-label={`Open ${prev_song || 'current song'} on Spotify`}><img className="current_art" src={CDN || spotify_logo} alt="" /></a>
                <div className="np-song-info"><div className="np-song-title">{prev_song || 'Waiting for the next song'}</div><div className="np-song-artist">{artistName || 'Your driver is getting the music ready'}</div><span className="spotify-attribution"><img src={spotify_logo} alt="" /> Spotify</span></div>
                <div className="buttons"><button className="play-pause-btn" onClick={togglePlayback} disabled={pending || !refreshToken} aria-label={paused ? 'Play music' : 'Pause music'}>{paused ? <PlayArrow /> : <Pause />}</button><Skip uid={uid} trackKey={track_url || prev_song} onStatus={setSkipStatus} /></div>
            </div>
            <p className={`player-status ${error ? 'error-text' : ''}`} role={error ? 'alert' : undefined}>{error || skipStatus}</p>
        </div>
        <PopupMenu uid={uid} vol_control={vol_control} isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </footer>;
}
